import './styles/kpi.css';
import { groupDashboardNavigation, staffHeaderNavigation } from './utils/dashboardNavigation.mjs';
import { pageAnchor } from './utils/pageAnchor.mjs';
import { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import AppRoutes from './routes/AppRoutes';
import { clearSubscriptionEntitlements, setNavLinks, setSubscriptionEntitlements } from './store/uiSlice';
import { getSubscription } from './services/billing';
import useAuthPersistence from './hooks/useAuthPersistence';
import ErrorBoundary from './components/ErrorBoundary';
import GlobalActivity from './components/brand/GlobalActivity';
import useShopTheme from './hooks/useShopTheme';
import useShopSocket from './hooks/useShopSocket';
import api from './services/api';
import { fetchShopSuccess } from './store/shopSlice';

const SHOP_ROLES = ['SHOP_OWNER', 'SHOP_STAFF', 'CHEF', 'DELIVERY', 'BEARER', 'HOTEL_USER'];

const App = () => {
  const dispatch = useDispatch();
  const { user } = useSelector((state) => state.auth);
  const currentUserId = useRef(user?.id);
  currentUserId.current = user?.id;
  const shopProfile = useSelector((state) => state.shop?.profile);
  const kioskEnabled = shopProfile?.kiosk?.enabled === true;
  const queueEnabled = shopProfile?.isQueueEnabled === true;
  const selfServiceEnabled = shopProfile?.isSelfServiceEnabled === true;
  const onlineOrderingEnabled = shopProfile?.isOnlineOrderingEnabled === true;
  const tableServiceEnabled = shopProfile?.isTableServiceEnabled === true;
  const hospitalModeEnabled = shopProfile?.hospitalModeEnabled === true;
  const bookingEnabled = shopProfile?.preferences?.roomPreorders?.enabled === true && shopProfile?.roomServiceOrderingEnabled !== false;
  const bookingDeliveryEnabled=bookingEnabled&&shopProfile?.preferences?.roomPreorders?.deliveryEnabled!==false;
  const hotelModeEnabled = shopProfile?.hotelModeEnabled === true;
  const alcoholEnabled = shopProfile?.alcoholicDrinksEnabled !== false;
  const location = useLocation();
  // The shop's resolved entitlement feature codes; null until loaded.
  const [features, setFeatures] = useState(null);

  useAuthPersistence();
  useShopTheme();
  useShopSocket(user?.shopId, { 'bar:license': async () => {
    if (!user || ['ADMIN', 'CUSTOMER', 'USER'].includes(user.role)) return;
    const loadingUserId = user.id;
    try { const { data } = await api.get('/shops/me'); if (currentUserId.current === loadingUserId) dispatch(fetchShopSuccess(data)); } catch { /* Keep the current profile until the next successful refresh. */ }
  } });

  useEffect(() => {
    if (window.__THATTUKADA_OWNER_PORTAL__ && ['/'].includes(location.pathname)) {
      window.location.replace('/');
    }
  }, [location.pathname]);

  // Load the shop's active feature set so nav can be entitlement-aware.
  useEffect(() => {
    let active = true;
    if (!user || !SHOP_ROLES.includes(user.role)) {
      setFeatures(null);
      dispatch(clearSubscriptionEntitlements());
      return undefined;
    }
    getSubscription()
      .then((data) => {
        if (!active) return;
        const resolved = data?.subscription?.features || [];
        setFeatures(resolved);
        dispatch(setSubscriptionEntitlements({ features: resolved, status: data?.subscription?.status }));
      })
      .catch(() => {
        if (!active) return;
        setFeatures([]);
        dispatch(setSubscriptionEntitlements({ features: [], status: 'UNAVAILABLE' }));
      });
    return () => { active = false; };
  }, [dispatch, user]);

  useEffect(() => {
    const baseLinks = [
      { to: '/', label: 'Home' },
      { to: '/about', label: 'About' },
      { to: '/features', label: 'Features' },
      { to: '/pricing', label: 'Pricing' }
    ];

    const adminLinks = [
      { to: '/dashboard', label: 'Dashboard' },
      { to: '/dashboard/shops', label: 'Shops' },
      { to: '/dashboard/food-plazas', label: 'Food Plazas' },
      { to: '/dashboard/users', label: 'Users' },
      { to: '/dashboard/catalog', label: 'Taxonomies' },
      { to: '/dashboard/alcoholic-drinks', label: 'Alcoholic Drinks' },
      { to: '/dashboard/bar-licenses', label: 'BAR Licenses' },
      { to: '/dashboard/finance', label: 'Finance' },
      { to: '/dashboard/pricing', label: 'Pricing' },
      { to: '/dashboard/settings', label: 'Settings' },
      { to: '/dashboard/reports', label: 'Reports' }
    ];

    // `anyOf` gates a link on the shop owning at least one of those features.
    const orderOperationsEnabled = kioskEnabled || queueEnabled || selfServiceEnabled || onlineOrderingEnabled || tableServiceEnabled || hospitalModeEnabled || hotelModeEnabled;
    const kitchenEnabled = kioskEnabled || selfServiceEnabled || onlineOrderingEnabled || tableServiceEnabled || hospitalModeEnabled || hotelModeEnabled;
    const shopOwnerLinks = [
      { to: '/shope-dashboard', label: 'Overview' },
      { to: '/dashboard/menu', label: 'Menu' },
      { to: '/dashboard/queue', label: 'Queue & Orders', anyOf: ['QUEUE', 'ORDERING', 'SELF_SERVICE'], moduleEnabled: orderOperationsEnabled },
      { to: '/dashboard/self-service', label: 'Self Service', anyOf: ['SELF_SERVICE'], moduleEnabled: selfServiceEnabled },
      { to: '/dashboard/kitchen', label: 'Kitchen', anyOf: ['ORDERING', 'SELF_SERVICE'], moduleEnabled: kitchenEnabled },
      { to: '/dashboard/liquor', label: 'Liquor Order', moduleEnabled: kitchenEnabled && alcoholEnabled },
      { to: '/dashboard/tables', label: 'Tables', anyOf: ['TABLE_SERVICE'], moduleEnabled: tableServiceEnabled },
      { to: '/dashboard/rooms', label: 'Rooms', anyOf: ['ROOM_SERVICE'], moduleEnabled: hotelModeEnabled },
      { to: '/dashboard/pre-orders', label: 'Booking', anyOf: ['ROOM_SERVICE'], moduleEnabled: hotelModeEnabled && bookingEnabled },
      {to:'/dashboard/booking-delivery',label:'Booking Delivery',anyOf:['ROOM_SERVICE'],moduleEnabled:hotelModeEnabled&&bookingDeliveryEnabled},
      { to: '/dashboard/bookings', label: 'View Bookings', anyOf: ['ROOM_SERVICE'], moduleEnabled: hotelModeEnabled && bookingEnabled },
      { to: '/dashboard/departments', label: 'Departments', anyOf: ['DEPARTMENT_MODE'], moduleEnabled: hospitalModeEnabled },
      { to: '/dashboard/delivery', label: 'Deliveries', anyOf: ['ORDERING'], moduleEnabled: onlineOrderingEnabled },
      { to: '/dashboard/settings', label: 'Settings' }
    ];

    const roleShopLinks = {
      SHOP_OWNER: shopOwnerLinks,
      SHOP_STAFF: shopOwnerLinks,
      CHEF: [
        { to: '/shope-dashboard', label: 'Overview' },
        { to: '/dashboard/kitchen', label: 'Kitchen', anyOf: ['ORDERING', 'SELF_SERVICE'], moduleEnabled: kitchenEnabled }
      ],
      DELIVERY: [
        { to: '/shope-dashboard', label: 'Overview' },
        { to: '/dashboard/delivery', label: 'Deliveries', anyOf: ['ORDERING'], moduleEnabled: onlineOrderingEnabled }
      ],
      HOTEL_USER: [
        { to: '/dashboard/rooms', label: 'Hotel Rooms', anyOf: ['ROOM_SERVICE'], moduleEnabled: hotelModeEnabled },
        { to: '/dashboard/tables', label: 'Hotel Tables', anyOf: ['TABLE_SERVICE'], moduleEnabled: tableServiceEnabled }
      ],
      BEARER: [
        { to: '/shope-dashboard', label: 'Overview' },
        { to: '/dashboard/queue', label: 'Queue & Orders', anyOf: ['QUEUE', 'ORDERING'], moduleEnabled: orderOperationsEnabled },
        { to: '/dashboard/tables', label: 'Tables', anyOf: ['TABLE_SERVICE'], moduleEnabled: tableServiceEnabled }
      ]
    };

    const cleanLinks = (links) => [
      ...links.filter(isAllowed).map(({ anyOf: _anyOf, moduleEnabled: _moduleEnabled, ...link }) => link)
    ];

    // Operational navigation requires both an active package entitlement and
    // the corresponding shop module switch. Ungated core pages remain visible.
    const featureSet = features ? new Set(features) : null;
    const isAllowed = (link) => {
      if (link.moduleEnabled === false) return false;
      if (!link.anyOf) return true;
      if (!featureSet) return false;
      return link.anyOf.some((f) => featureSet.has(f));
    };
    const linksByRole = {
      CUSTOMER: [...baseLinks, { to: '/customer/orders', label: 'My orders' }, { to: '/customer/profile', label: 'My profile' }],
      USER: [...baseLinks, { to: '/customer/orders', label: 'My orders' }, { to: '/customer/profile', label: 'My profile' }],
      ADMIN: adminLinks,
      FOOD_PLAZA: [{ to: '/dashboard/food-plaza', label: 'Food Plaza profile' }, { to: '/dashboard/food-plaza/shops', label: 'Shops' }],
      SHOP_OWNER: [...(shopProfile?.purchaseStockEnabled ? [{to:'/dashboard/purchase-stock',label:'Purchase & Stock'},{to:'/dashboard/stock-reports',label:'Stock & Management'}] : []), ...cleanLinks(roleShopLinks.SHOP_OWNER), ...cleanLinks([{ to: '/dashboard/shop-reports', label: 'Sales & products', anyOf: ['ANALYTICS'] }, { to: '/dashboard/shop-reports/feedback', label: 'Customer feedback', anyOf: ['ANALYTICS'] }]), { to: '/dashboard/cash-report', label: 'Cash in hand' }, { to: '/dashboard/shop-finance', label: 'Finance' }, { to: '/dashboard/staff', label: 'Users' }],
      SHOP_OPERATOR: staffHeaderNavigation([],user?.staffModules||[]),
      SHOP_STAFF: cleanLinks(roleShopLinks.SHOP_STAFF),
      CHEF: cleanLinks(roleShopLinks.CHEF),
      DELIVERY: cleanLinks(roleShopLinks.DELIVERY),
      HOTEL_USER: cleanLinks(roleShopLinks.HOTEL_USER),
      BEARER: cleanLinks(roleShopLinks.BEARER),
      default: baseLinks
    };

    const allowedLinks = linksByRole[user?.role] || linksByRole.default;
    dispatch(setNavLinks(user?.role==='SHOP_OPERATOR' ? staffHeaderNavigation([],user.staffModules||[]) : SHOP_ROLES.includes(user?.role) ? groupDashboardNavigation(allowedLinks) : allowedLinks));
  }, [dispatch, user, features, bookingEnabled, bookingDeliveryEnabled, kioskEnabled, queueEnabled, selfServiceEnabled, onlineOrderingEnabled, tableServiceEnabled, hospitalModeEnabled, hotelModeEnabled, alcoholEnabled, shopProfile?.purchaseStockEnabled]);

  useEffect(() => {
    if (location.hash) {
      const target = pageAnchor(document, {pathname:location.pathname,hash:location.hash});
      if (target) {
        window.setTimeout(() => target.scrollIntoView({ behavior: 'smooth', block: 'start' }), 0);
      }
      return;
    }
    try {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (error) {
      console.warn('Smooth scroll not supported, falling back', error);
      window.scrollTo(0, 0);
    }
  }, [location.pathname, location.hash]);

  useEffect(() => {
    if (typeof window === 'undefined' || typeof document === 'undefined') return undefined;

    const root = document.documentElement;
    const popupSelector = [
      '.modal.show .modal-dialog',
      '.modal.show .modal-content',
      '.offcanvas.show',
      '.offcanvas.showing',
      '.aip-overlay .aip-modal'
    ].join(', ');
    let animationFrame = 0;
    const resizeObserver = typeof ResizeObserver === 'function' ? new ResizeObserver(() => scheduleMeasure()) : null;

    const measurePopupHeight = () => {
      const popups = Array.from(document.querySelectorAll(popupSelector));
      resizeObserver?.disconnect();

      const maxHeight = popups.reduce((height, element) => {
        resizeObserver?.observe(element);
        const rect = element.getBoundingClientRect();
        const scrollHeight = element.scrollHeight || 0;
        return Math.max(height, rect.height, scrollHeight);
      }, 0);

      if (maxHeight > 0) {
        root.classList.add('has-active-popup');
        root.style.setProperty('--active-popup-height', `${Math.ceil(maxHeight)}px`);
      } else {
        root.classList.remove('has-active-popup');
        root.style.removeProperty('--active-popup-height');
      }
    };

    function scheduleMeasure() {
      window.cancelAnimationFrame(animationFrame);
      animationFrame = window.requestAnimationFrame(measurePopupHeight);
    }

    const mutationObserver = new MutationObserver(scheduleMeasure);
    mutationObserver.observe(document.body, {
      attributes: true,
      childList: true,
      subtree: true,
      attributeFilter: ['class', 'style']
    });
    window.addEventListener('resize', scheduleMeasure);
    scheduleMeasure();

    return () => {
      window.cancelAnimationFrame(animationFrame);
      window.removeEventListener('resize', scheduleMeasure);
      mutationObserver.disconnect();
      resizeObserver?.disconnect();
      root.classList.remove('has-active-popup');
      root.style.removeProperty('--active-popup-height');
    };
  }, []);

  return (
    <>
      <Helmet>
        <meta property="og:title" content="SmartMenu" />
        <meta property="og:description" content="Unified digital menu and ordering platform for India's street food vendors." />
      </Helmet>
      <ErrorBoundary onReset={() => window.location.reload()}>
        <AppRoutes />
      </ErrorBoundary>
      <GlobalActivity />
    </>
  );
};

export default App;
